import { useState } from 'react'
import Modal from '../common/Modal'
import mpesaLogo from '../../assets/mpesa-logo.png'
import { useAuth } from '../../contexts/AuthContext'
import { useMutation, useQuery } from '@tanstack/react-query'
import { handleGetRequest, handlePostRequest } from '../../utils/api-handler.util'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import LoadingSpinner from '../common/LoadingSpinner'
import {
  PAYMENT_EXPIRY_KEY,
  PAYMENT_ID_EXPIRY_MILLISECONDS,
  PAYMENT_ID_KEY,
} from '../../constants/payment'
import PhoneInput from '../common/PhoneInput'

const VerificationPaymentModal = ({ isOpen, onClose }) => {
  const { currentUser } = useAuth()
  const [phoneNumber, setPhoneNumber] = useState(() => currentUser?.phoneNumber)

  const { data: verificationFeeData } = useQuery({
    queryKey: QueryKeys.verificationFee(),
    queryFn: () => handleGetRequest(`/${currentUser.userType}s/verification-fee`),
  })

  const verificationFee = verificationFeeData?.data

  const payVerificationMutation = useMutation({
    mutationFn: async (paymentData) =>
      handlePostRequest(`/${currentUser.userType}s/pay-verification`, {
        phoneNumber: paymentData.phoneNumber,
      }),
    onSuccess: (response) => {
      localStorage.setItem(PAYMENT_ID_KEY, response.data.id)
      localStorage.setItem(PAYMENT_EXPIRY_KEY, Date.now() + PAYMENT_ID_EXPIRY_MILLISECONDS)
      onClose(true)
      QueryKeys.shopVerificationDetails.invalidate(currentUser.id)
    },
    onError: (error) => {
      console.error('Verification payment failed:', error)
    },
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    await payVerificationMutation.mutateAsync({ phoneNumber })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col items-center relative mx-auto">
        <img src={mpesaLogo} alt="Mpesa" className="w-32 h-auto object-contain mb-4" />

        <h2 className="text-2xl font-bold text-center mb-2">Pay for Shop Verification</h2>
        <p className="text-center text-sm text-gray-600 mb-2">
          An M-Pesa prompt will be sent to your phone to complete payment.
        </p>

        <form onSubmit={handleSubmit} className="w-full space-y-6">
          <div className="bg-neutral-200-50 rounded-lg p-4 space-y-4">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Verification Fee</span>
              <span className="text-lg font-semibold text-orange-600">{verificationFee}</span>
            </div>

            <PhoneInput
              label="M-Pesa Phone Number"
              id="phoneNumber"
              value={phoneNumber}
              onChange={(phone) => setPhoneNumber(phone)}
              required
            />
          </div>

          <div className="space-y-3 pt-2">
            <button
              type="submit"
              disabled={payVerificationMutation.isPending}
              className="w-full h-12 bg-gradient-to-r from-secondary-700 to-secondary-500 text-white text-lg font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50"
            >
              {payVerificationMutation.isPending ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <LoadingSpinner />
                  Processing...
                </div>
              ) : (
                `Pay ${verificationFee}`
              )}
            </button>

            <button
              type="button"
              onClick={onClose}
              disabled={payVerificationMutation.isPending}
              className="w-full h-12 border-2 border-gray-300 text-gray-700 text-lg font-medium rounded-lg hover:border-gray-400 hover:bg-neutral-200-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default VerificationPaymentModal
